<template>
    <div v-if="hasPermissionTo('list ring group')">
        <label class="p-0 m-0 _500"
               v-if="label">
            {{ label }}
        </label>
        <el-tooltip effect="dark"
                    placement="top"
                    :content="disabled_tooltip_content"
                    :disabled="!disabled">
            <el-select class="w-full"
                       autocomplete="off"
                       :clearable="ring_group_id !== 'none'"
                       filterable
                       :default-first-option="default_first"
                       :multiple="multiple"
                       :placeholder="placeholder"
                       :size="size"
                       :disabled="disabled"
                       :collapse-tags="collapsable"
                       data-testid="ring-group-selector"
                       v-model="ring_group_id"
                       @clear="ringGroupChange"
                       @change="ringGroupChange">
                <el-select-bulk-actions 
                    v-if="showBulkActions && multiple" 
                    custom-class="ml-2 mb-2" 
                    @selectAll="selectAll" 
                    @clearSelection="clearSelection" 
                />
                <el-option :key="ring_group.id"
                           :label="ring_group.name"
                           :value="ring_group.id"
                           v-for="ring_group in ring_group_list">
                    <ring-group-icon 
                        v-if="showIcons"
                        class="mr-1"
                        :size="26"
                        :type="ring_group.is_personal_inbox ? 'personal' : 'team'"
                    />
                    <span>{{ ring_group.name }}</span>
                </el-option>
            </el-select>
        </el-tooltip>
    </div>
</template>

<script>
    import {mapState} from 'vuex'
    import auth from '../auth'
    import {acl_mixin} from '../mixins'
    import RingGroupIcon from './ring-groups/icons/ring-group-icon.vue'
    import ElSelectBulkActions from './el-select-bulk-actions.vue'

    export default {
        mixins: [acl_mixin],

        components: {
            RingGroupIcon,
            ElSelectBulkActions
        },

        props: {
            value: {
                required: false,
            },

            multiple: {
                type: Boolean,
                required: false,
                default: false
            },

            default_first: {
                type: Boolean,
                required: false,
                default: false
            },

            first_value: {
                type: String,
                required: false,
                default: null
            },

            label: {
                required: false,
                default: null
            },

            size: {
                type: String,
                default: '',
                required: false
            },

            collapsable: {
                type: Boolean,
                required: false,
                default: false
            },

            disabled: {
                type: Boolean,
                required: false,
                default: false
            },

            disabled_tooltip_content: {
                type: String,
                required: false,
                default: null
            },

            customPlaceholder: {
                type: String,
                required: false
            },

            noneAsDefault: {
                type: Boolean,
                required: false,
                default: false
            },

            includePersonalInboxes: {
                type: Boolean,
                required: false,
                default: false
            },

            showIcons: {
                type: Boolean,
                required: false,
                default: false
            },

            showBulkActions: {
                type: Boolean,
                required: false,
                default: false
            }
        },

        data() {
            return {
                auth: auth,
                ring_group_id: this.value ? this.value : (this.noneAsDefault ? 'none' : null),
                ring_group_list: []
            }
        },

        computed: {
            ...mapState({
                current_company: state => state.cache.current_company,
                ring_groups: state => state.ring_groups
            }),

            filteredRingGroups() {
                const deleted_pattern = /_deleted_\d+$/

                const ring_groups = !this.includePersonalInboxes
                    ? this.ring_groups.filter(ring_group => !ring_group.is_personal_inbox)
                    : this.ring_groups.filter(ring_group => !deleted_pattern.test(ring_group.name))

                if (this.noneAsDefault) {
                    ring_groups.unshift({ name: 'None', id: 'none', user_ids: [] })
                }

                return ring_groups
            },

            placeholder() {
                if (this.customPlaceholder) {
                    return this.customPlaceholder
                }

                if (this.multiple) {
                    return 'Select Inboxes'
                }

                return 'Select Inbox'
            },
        },

        mounted() {
            this.updateRingGroupList()
        },

        created() {
            if (this.noneAsDefault && !this.value) {
                this.ring_group_id = 'none'
                this.$nextTick(() => {
                    this.$emit('change', 'none')
                })
            }
        },

        watch: {
            value() {
                if (this.noneAsDefault && !this.value) {
                    this.ring_group_id = 'none'
                } else {
                    this.ring_group_id = this.value
                }
            },

            ring_group_id(val) {
                if (this.value !== undefined) {
                    this.$emit('change', val)
                }
            },

            filteredRingGroups: {
                deep: true,
                handler: function() {
                    this.updateRingGroupList()
                }
            }
        },

        methods: {
            ringGroupChange(ring_group_id) {
                if (!ring_group_id && this.first_value && !this.noneAsDefault) {
                    ring_group_id = 'all'
                    this.ring_group_id = 'all'
                }

                if (this.noneAsDefault && !ring_group_id) {
                    ring_group_id = 'none'
                    this.ring_group_id = 'none'
                }

                this.$emit('change', ring_group_id)
            },

            updateRingGroupList() {
                if (this.first_value) {
                    const all = [{name: this.first_value.capitalize(), id: this.first_value, user_ids: []}]
                    this.ring_group_list = _.concat(all, this.filteredRingGroups)
                    return
                }

                this.ring_group_list = this.filteredRingGroups
            },

            selectAll() {
                this.ringGroupChange(this.filteredRingGroups.map(ring_group => ring_group.id))
            },

            clearSelection() {
                this.ringGroupChange([])
            },
        },
    }
</script>
